/**
 * HistoryPopup — a job's audit lines, out of the way of the working page.
 *
 * Used by the Dispatch tab (dispatch log lines + contractor payments) and the
 * Finalize tab's Contractor section (contractor pay history + reversed
 * commissions). Same look as the Accounting "View all" popup: newest first,
 * date, description, the change in money where there is one, 25 to a page.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { CRM } from '../lib/theme';
import { formatDate, formatDateTime, formatMoney } from '../lib/utils';

const C = { ...CRM, mono: "'DM Mono', monospace" };

export interface HistoryRow {
  id: string;
  /** 'YYYY-MM-DD' or an ISO timestamp. */
  at: string | null | undefined;
  text: string;
  /** Signed change in money; null / undefined = none. */
  change?: number | null;
  /** A muted tag after the text ("accepted", "e-Transfer"). */
  note?: string | null;
}

const PAGE = 25;

/**
 * The money change a log line states: its LAST "$a → $b" pair (a pay change's
 * total comes last — "… line $10 → $20 (total $500 → $510)"). null if none.
 */
export function changeFromText(text: string | null | undefined): number | null {
  const pairs = [...String(text ?? '').matchAll(/\$([\d,]+(?:\.\d+)?)\s*→\s*\$([\d,]+(?:\.\d+)?)/g)];
  const last = pairs[pairs.length - 1];
  if (!last) return null;
  const n = (s: string) => Number(s.replace(/,/g, ''));
  const d = Math.round((n(last[2]) - n(last[1])) * 100) / 100;
  return Number.isFinite(d) && d !== 0 ? d : null;
}

const when = (at: string | null | undefined) =>
  !at ? '—' : /^\d{4}-\d{2}-\d{2}$/.test(at) ? formatDate(at) : formatDateTime(at);

export function HistoryButton({ onClick, count, label = 'History' }: { onClick: () => void; count?: number; label?: string }) {
  return (
    <button onClick={onClick}
      style={{ padding: '4px 10px', background: 'transparent', color: C.accent, border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {label}{count ? ` (${count})` : ''}
    </button>
  );
}

export function HistoryPopup({ title, sub, rows, loading, error, onClose }: {
  title: string; sub?: string; rows: HistoryRow[] | null; loading?: boolean; error?: string | null; onClose: () => void;
}) {
  const [page, setPage] = useState(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const sorted = useMemo(() => [...(rows ?? [])].sort((a, b) => String(b.at ?? '').localeCompare(String(a.at ?? ''))), [rows]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const shown = sorted.slice(page * PAGE, (page + 1) * PAGE);
  const th = (align: 'left' | 'right'): React.CSSProperties => ({ padding: '8px 12px', fontSize: '9px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', textAlign: align, borderBottom: `1px solid ${C.border}` });
  const td: React.CSSProperties = { padding: '8px 12px', fontSize: '12px', borderBottom: `1px solid ${C.panel2}`, verticalAlign: 'top' };
  const navBtn = (disabled: boolean): React.CSSProperties => ({ padding: '5px 11px', background: C.panel2, color: disabled ? C.dim : C.text, border: `1px solid ${C.border}`, borderRadius: '6px', cursor: disabled ? 'default' : 'pointer', fontSize: '12px' });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px', width: '820px', maxWidth: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: C.text, margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: C.muted }}>✕</button>
        </div>
        {sub && <div style={{ fontSize: '11px', color: C.muted, marginBottom: '12px' }}>{sub}</div>}
        {error && <div style={{ fontSize: '12px', color: C.red, marginBottom: '8px' }}>{error}</div>}
        <div style={{ overflow: 'auto', flex: 1, background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th('left')}>Date</th><th style={th('left')}>Description</th><th style={th('right')}>Change</th></tr></thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={3} style={{ ...td, textAlign: 'center', color: C.dim, padding: '18px' }}>{loading || rows == null ? 'Loading…' : 'No history yet.'}</td></tr>
              )}
              {shown.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...td, fontFamily: C.mono, color: C.muted, whiteSpace: 'nowrap' }}>{when(r.at)}</td>
                  <td style={{ ...td, color: C.text }}>
                    {r.text}
                    {r.note && <span style={{ color: C.dim, marginLeft: '8px' }}>{r.note}</span>}
                  </td>
                  <td style={{ ...td, fontFamily: C.mono, textAlign: 'right', whiteSpace: 'nowrap', color: r.change != null && r.change < 0 ? C.red : C.text }}>
                    {r.change == null || r.change === 0 ? '—' : `${r.change > 0 ? '+' : '−'}${formatMoney(Math.abs(r.change))}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sorted.length > PAGE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', fontSize: '12px', color: C.muted }}>
            <span>{page * PAGE + 1}–{Math.min(sorted.length, (page + 1) * PAGE)} of {sorted.length}</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={navBtn(page === 0)}>‹ Newer</button>
              <span>Page {page + 1} of {pages}</span>
              <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} style={navBtn(page + 1 >= pages)}>Older ›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
